import { useEffect, useState } from 'react';
import { http } from '../../api';
import Select from '../../components/common/Select';

// Settings → AI Audit. Shows the dispatcher's invocation log for this
// chambers + lets admins disable specific tools and tune the daily write
// budget. Tenant-scoped server-side — chambers can only see their own log.

type Invocation = {
  id: string | number;
  user_id: string | number;
  tool_name: string;
  capability: string | null;
  matter_id: string | null;
  outcome: 'ok' | 'forbidden' | 'not_found' | 'tenant_violation' | 'missing_confirmation' | 'exec_error';
  error_code: string | null;
  duration_ms: number | null;
  started_at: string;
  finished_at: string | null;
  args: unknown;
};

const ALL_TOOLS = [
  // mutating tools tenants might want to disable
  'createInvoiceFromWip', 'createTask', 'logTimeEntry', 'createDiaryEvent',
  'advanceMatterStage', 'saveDraft', 'createAttorney',
];

export default function AIAudit() {
  const [invocations, setInvocations] = useState<Invocation[]>([]);
  const [outcome, setOutcome] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState<string[]>([]);
  const [budget, setBudget] = useState<number>(200);
  const [savingControls, setSavingControls] = useState(false);
  const [controlsMsg, setControlsMsg] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    const qs = outcome ? `?outcome=${encodeURIComponent(outcome)}` : '';
    http.get<{ invocations: Invocation[] }>(`/ai/tool-audit${qs}`)
      .then((r) => setInvocations(r.invocations || []))
      .catch(() => setInvocations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [outcome]);

  const saveControls = async () => {
    setSavingControls(true);
    setControlsMsg(null);
    try {
      await http.put('/ai/tool-controls', {
        disabled_tools: disabled,
        daily_write_budget: budget,
      });
      setControlsMsg('Saved.');
    } catch (e) {
      setControlsMsg((e as Error).message);
    } finally {
      setSavingControls(false);
    }
  };

  const toggleTool = (name: string) => {
    setDisabled((cur) => cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]);
  };

  return (
    <div className="stack">
      <div>
        <h1>AI tool audit</h1>
        <p className="muted" style={{ fontSize: 13 }}>
          Every AI tool call is logged here, including denials. Tenant-scoped: this chambers cannot see other chambers' logs.
        </p>
      </div>

      <div className="card stack">
        <h3 style={{ margin: 0 }}>Tool controls</h3>
        <div>
          <label>Disabled tools (AI cannot call these for this chambers)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {ALL_TOOLS.map((t) => (
              <label key={t} className="inline">
                <input
                  type="checkbox"
                  checked={disabled.includes(t)}
                  onChange={() => toggleTool(t)}
                />
                <code style={{ fontSize: 12 }}>{t}</code>
              </label>
            ))}
          </div>
        </div>
        <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
          <div>
            <label>Daily write-budget per user</label>
            <input
              type="number"
              min={0}
              max={5000}
              value={budget}
              onChange={(e) => setBudget(Math.max(0, Number(e.target.value) || 0))}
              style={{ width: 120 }}
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Soft cap on AI mutating-tool calls per user per day (default 200).
            </div>
          </div>
          <button className="primary" onClick={saveControls} disabled={savingControls}>
            {savingControls ? 'Saving…' : 'Save controls'}
          </button>
          {controlsMsg && <span className="muted" style={{ fontSize: 12 }}>{controlsMsg}</span>}
        </div>
      </div>

      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Recent invocations</h3>
        <Select
          value={outcome}
          onChange={(v) => setOutcome(v)}
          style={{ width: 220 }}
          options={[
            { value: '', label: 'All outcomes' },
            { value: 'ok', label: 'OK' },
            { value: 'forbidden', label: 'Forbidden' },
            { value: 'not_found', label: 'Not found' },
            { value: 'tenant_violation', label: 'Tenant violation (!)' },
            { value: 'missing_confirmation', label: 'Missing confirmation' },
            { value: 'exec_error', label: 'Exec error' },
          ]}
        />
        <button onClick={load}>Refresh</button>
      </div>

      <div className="card">
        {loading ? <div className="muted">Loading…</div>
          : invocations.length === 0 ? <p className="muted">No invocations yet.</p>
          : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Tool</th>
                <th>Capability</th>
                <th>Matter</th>
                <th>Outcome</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {invocations.map((i) => (
                <tr key={String(i.id)}>
                  <td style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{i.started_at}</td>
                  <td>{String(i.user_id)}</td>
                  <td><code style={{ fontSize: 12 }}>{i.tool_name}</code></td>
                  <td className="muted" style={{ fontSize: 12 }}>{i.capability || '—'}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{i.matter_id || '—'}</td>
                  <td>
                    <span className={`pill ${i.outcome === 'tenant_violation' ? 'danger' : i.outcome === 'ok' ? '' : ''}`}>
                      {i.outcome}{i.error_code ? ` · ${i.error_code}` : ''}
                    </span>
                  </td>
                  <td>{i.duration_ms != null ? `${i.duration_ms}ms` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
